import {useSuspenseInfiniteQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import React, {useEffect} from 'react';
import {MAIN_TRIP_DEFAULT_PAGE, MAIN_TRIP_SIZE, TripTab} from '@di-shared/features/safe-driving/kr/SafeDrivingMainTrip.kr';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import SafeDrivingMainTripItemKr from '@di-shared/features/safe-driving/kr/SafeDrivingMainTripItem.kr';
import SafeDrivingMainTripItemLoadingKr from '@di-shared/features/safe-driving/kr/SafeDrivingMainTripItemLoading.kr';

export interface SafeDrivingMainTripListKrProps {
  vin: string;
  selectedTripTab: TripTab;
  isFetchMore: boolean;

  onUpdateFetchMore(): void;
  onUpdateIsTripAvailable(isTripAvailable: boolean): void;
}

function SafeDrivingMainTripListKr({
  vin,
  selectedTripTab,
  isFetchMore,
  onUpdateFetchMore,
  onUpdateIsTripAvailable,
}: SafeDrivingMainTripListKrProps) {
  const {t} = useInferableTranslation();
  const {
    data: mainTripData,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useSuspenseInfiniteQuery({
    queryKey: [getQueryKey(diApi.driveHistory.getMainTrip), vin, selectedTripTab],
    queryFn: ({pageParam}) =>
      diApi.driveHistory.getMainTrip({
        vin,
        isSafe: selectedTripTab === TripTab.SAFE_DRIVING,
        size: MAIN_TRIP_SIZE,
        page: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.data.currentPage < lastPage.data.totalPageCnt ? MAIN_TRIP_DEFAULT_PAGE + 1 : undefined;
    },
  });
  useEffect(() => {
    onUpdateIsTripAvailable(mainTripData.pages[0].data.totalDrivingCnt > 0 && mainTripData.pages[0].data.mainTripExists);
  }, [mainTripData]);

  return mainTripData.pages[0].data.totalTripCnt === 0 ? (
    <div className="empty_area">
      <span className="ic ic_caution_32px"></span>
      <span
        className="body_small_r"
        style={{whiteSpace: 'pre-wrap'}}
      >
        {selectedTripTab === TripTab.RECKLESS_DRIVING ? t('T20214') : t('T20215')}
      </span>
    </div>
  ) : (
    <div className="tab_content driving_danger">
      <ul className="major_driving_list">
        {mainTripData.pages
          .flatMap((tripList) => tripList.data.trips)
          .slice(0, isFetchMore ? 6 : 3)
          .map((tripItem) => (
            <SafeDrivingMainTripItemKr
              key={tripItem.id}
              vin={vin}
              tripItem={tripItem}
            />
          ))}
        {isFetching && <SafeDrivingMainTripItemLoadingKr />}
      </ul>

      {mainTripData.pages[0].data.hasNextPage && mainTripData.pages[0].data.trips.length > 2 && (
        <button
          type="button"
          className="btn_unfold"
          aria-expanded={isFetchMore}
          aria-controls="majorDrivingDangerMore"
          onClick={() => {
            onUpdateFetchMore();
            if (hasNextPage) {
              fetchNextPage();
            }
          }}
        >
          <span className="unfold_text">{t('T20134')}</span>
          <span className="fold_text">{t('T20135')}</span>
          <span className="ic ic_arrow_down_14px"></span>
        </button>
      )}
    </div>
  );
}

export default SafeDrivingMainTripListKr;
